import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  SkipForward,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/features/auth';
import { type Language } from '@/types';
import {
  type CleanerJobBlock,
  type CleanerVisitJob,
  loadCleanerJobs,
  updateCleanerJobBlockStatus,
} from '../api';

interface CleanerWorkspaceProps {
  language: Language;
}

type NoticeState = {
  tone: 'success' | 'error';
  text: string;
};

function todayKey() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function visitProgress(visit: CleanerVisitJob) {
  const actionable = visit.blocks.filter((block) => block.status !== 'cancelled').length;
  const done = visit.blocks.filter((block) => block.status === 'completed' || block.status === 'skipped').length;
  return { actionable, done };
}

function groupByDate(visits: CleanerVisitJob[]) {
  const grouped = new Map<string, CleanerVisitJob[]>();
  for (const visit of visits) {
    const current = grouped.get(visit.scheduledFor) ?? [];
    current.push(visit);
    grouped.set(visit.scheduledFor, current);
  }
  return [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}

function InlineNotice({ notice }: { notice: NoticeState | null }) {
  if (!notice) return null;
  return (
    <div
      className={
        notice.tone === 'success'
          ? 'rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700'
          : 'rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'
      }
    >
      {notice.text}
    </div>
  );
}

type BlockCollapsibleProps = {
  block: CleanerJobBlock;
  language: Language;
  activeBlockId: string | null;
  blockNotes: Record<string, string>;
  onSubmitStatus: (blockId: string, status: CleanerJobBlock['status']) => void;
  onNotesChange: (blockId: string, value: string) => void;
};

const BlockCollapsible: React.FC<BlockCollapsibleProps> = ({
  block,
  language,
  activeBlockId,
  blockNotes,
  onSubmitStatus,
  onNotesChange,
}) => {
  const isTerminal = block.status !== 'planned';
  const [isOpen, setIsOpen] = useState(!isTerminal);

  const titleBg = (() => {
    if (block.status === 'completed') return 'bg-emerald-50 border-emerald-200';
    if (block.status === 'skipped') return 'bg-amber-50 border-amber-200';
    if (block.status === 'cancelled') return 'bg-muted/50 border-muted';
    return '';
  })();

  return (
    <div className={`rounded-lg border ${titleBg || ''}`}>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        onClick={() => setIsOpen(!isOpen)}
      >
        {block.status === 'completed' ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
        ) : block.status === 'skipped' ? (
          <SkipForward className="h-4 w-4 shrink-0 text-amber-600" />
        ) : block.status === 'cancelled' ? (
          <XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 space-y-0.5">
          <div className={`font-medium ${block.status === 'cancelled' ? 'line-through text-muted-foreground' : ''}`}>
            {block.area}
          </div>
          <div className={`text-sm ${block.status === 'cancelled' ? 'line-through text-muted-foreground' : 'text-muted-foreground'}`}>
            {block.label}
          </div>
        </div>
        {block.requiredPhotos > 0 && (
          <Badge variant="outline">
            {block.requiredPhotos} {language === 'sv' ? 'foto' : 'photos'}
          </Badge>
        )}
      </button>

      {isOpen && block.status === 'planned' && (
        <div className="space-y-3 border-t px-4 pb-4 pt-3">
          <Textarea
            value={blockNotes[block.id] ?? block.completionNotes ?? ''}
            onChange={(event) => onNotesChange(block.id, event.target.value)}
            placeholder={language === 'sv' ? 'Kommentar eller avvikelse…' : 'Add a note or deviation…'}
            rows={3}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSubmitStatus(block.id, 'completed')}
              disabled={activeBlockId === block.id}
            >
              {activeBlockId === block.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {language === 'sv' ? 'Klarmarkera' : 'Complete'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSubmitStatus(block.id, 'skipped')}
              disabled={activeBlockId === block.id}
            >
              <SkipForward className="h-4 w-4" />
              {language === 'sv' ? 'Hoppa över' : 'Skip'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSubmitStatus(block.id, 'cancelled')}
              disabled={activeBlockId === block.id}
            >
              <XCircle className="h-4 w-4" />
              {language === 'sv' ? 'Avbryt' : 'Cancel'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

type VisitCardProps = {
  visit: CleanerVisitJob;
  language: Language;
  onClick: () => void;
};

const VisitCard: React.FC<VisitCardProps> = ({
  visit,
  language,
  onClick,
}) => {
  const progress = visitProgress(visit);
  const isCompleted = visit.status === 'completed' || visit.status === 'approved';

  return (
    <button
      type="button"
      className={`w-full rounded-md border px-4 py-3 text-left transition-colors hover:bg-muted/40 ${isCompleted ? 'border-emerald-200 bg-emerald-50/30' : ''}`}
      onClick={onClick}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="font-medium">{visit.siteName}</div>
          <div className="text-sm text-muted-foreground">{visit.scheduledFor}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isCompleted && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          <Badge variant={visit.status === 'approved' ? 'success' : visit.status === 'completed' ? 'warning' : 'secondary'}>
            {visit.status === 'completed'
              ? (language === 'sv' ? 'Väntar godkännande' : 'Pending approval')
              : visit.status === 'approved'
                ? (language === 'sv' ? 'Godkänd' : 'Approved')
                : visit.status}
          </Badge>
          <Badge variant="outline">
            {progress.done}/{progress.actionable}
          </Badge>
        </div>
      </div>
    </button>
  );
}

export function CleanerWorkspace({ language }: CleanerWorkspaceProps) {
  const { session, profile } = useAuth();
  const [jobs, setJobs] = useState<CleanerVisitJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [blockNotes, setBlockNotes] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const sessionToken = session?.access_token ?? null;

  const refreshJobs = async (mode: 'load' | 'refresh' = 'refresh') => {
    if (mode === 'refresh') {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const nextJobs = await loadCleanerJobs(sessionToken);
      setJobs(nextJobs);
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Could not load assigned jobs.',
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    void refreshJobs('load');
  }, [sessionToken]);

  const today = todayKey();
  const todayJobs = useMemo(
    () => jobs.filter((job) => job.scheduledFor === today && job.status === 'assigned'),
    [jobs, today],
  );
  const upcomingJobs = useMemo(
    () => jobs.filter((job) => job.scheduledFor > today && job.status === 'assigned'),
    [jobs, today],
  );
  const completedJobs = useMemo(
    () => jobs.filter((job) => job.status === 'completed' || job.status === 'approved'),
    [jobs],
  );
  const selectedVisit = jobs.find((job) => job.id === selectedVisitId) ?? null;

  const submitBlockStatus = async (blockId: string, status: CleanerJobBlock['status']) => {
    setActiveBlockId(blockId);
    setNotice(null);

    try {
      await updateCleanerJobBlockStatus(
        {
          jobBlockId: blockId,
          status,
          completionNotes: blockNotes[blockId] ?? '',
        },
        sessionToken,
      );
      await refreshJobs();
      setNotice({
        tone: 'success',
        text: status === 'completed'
          ? (language === 'sv' ? 'Block markerat som klart.' : 'Block marked as complete.')
          : status === 'skipped'
            ? (language === 'sv' ? 'Block hoppades över.' : 'Block skipped.')
            : (language === 'sv' ? 'Block avbrutet.' : 'Block cancelled.'),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Could not update block status.',
      });
    } finally {
      setActiveBlockId(null);
    }
  };

  const handleNotesChange = (blockId: string, value: string) => {
    setBlockNotes((current) => ({ ...current, [blockId]: value }));
  };

  if (selectedVisit) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-background">
        <header className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-4 py-3">
          <Button type="button" variant="ghost" size="icon" onClick={() => setSelectedVisitId(null)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 space-y-0.5">
            <div className="font-semibold leading-tight">{selectedVisit.siteName}</div>
            <div className="text-xs text-muted-foreground">
              {selectedVisit.scheduledFor} · {selectedVisit.totalPoints.toFixed(2)} pts
            </div>
          </div>
          <Badge variant={selectedVisit.status === 'approved' ? 'success' : selectedVisit.status === 'completed' ? 'warning' : 'secondary'}>
            {selectedVisit.status === 'completed'
              ? (language === 'sv' ? 'Väntar godkännande' : 'Pending')
              : selectedVisit.status === 'approved'
                ? (language === 'sv' ? 'Godkänd' : 'Approved')
                : selectedVisit.status}
          </Badge>
        </header>

        <InlineNotice notice={notice} />

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {selectedVisit.blocks.map((block) => (
            <BlockCollapsible
              key={block.id}
              block={block}
              language={language}
              activeBlockId={activeBlockId}
              blockNotes={blockNotes}
              onSubmitStatus={submitBlockStatus}
              onNotesChange={handleNotesChange}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{language === 'sv' ? 'Mina jobb' : 'My jobs'}</h1>
        <p className="text-sm text-muted-foreground">
          {language === 'sv'
            ? 'Dina tilldelade jobb. Öppna ett jobb för att markera block som klara.'
            : 'Your assigned jobs. Open a job to update block statuses.'}
        </p>
      </header>

      <div className="flex items-center justify-between gap-3 rounded-md border bg-card px-4 py-3">
        <div>
          <div className="text-sm font-medium">{profile?.full_name ?? profile?.email ?? 'Cleaner'}</div>
          <div className="text-xs text-muted-foreground">
            {language === 'sv' ? 'Roll: städare' : 'Role: cleaner'}
          </div>
        </div>
        <Button type="button" variant="outline" onClick={() => void refreshJobs()} disabled={isRefreshing || isLoading}>
          {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {language === 'sv' ? 'Uppdatera' : 'Refresh'}
        </Button>
      </div>

      <InlineNotice notice={notice} />

      {isLoading ? (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
          {language === 'sv' ? 'Laddar tilldelade jobb…' : 'Loading assigned jobs…'}
        </div>
      ) : (
        <Tabs defaultValue="today">
          <TabsList className="w-full">
            <TabsTrigger value="today" className="flex-1">
              {language === 'sv' ? 'Idag' : 'Today'}
              {todayJobs.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({todayJobs.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="flex-1">
              {language === 'sv' ? 'Kommande' : 'Upcoming'}
              {upcomingJobs.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({upcomingJobs.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex-1">
              {language === 'sv' ? 'Jobb' : 'Jobs'}
              {completedJobs.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">({completedJobs.length})</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today" className="space-y-3 pt-3">
            {todayJobs.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                {language === 'sv' ? 'Inga jobb tilldelade idag.' : 'No jobs assigned today.'}
              </div>
            ) : (
              todayJobs.map((visit) => (
                <VisitCard key={visit.id} visit={visit} language={language} onClick={() => setSelectedVisitId(visit.id)} />
              ))
            )}
          </TabsContent>

          <TabsContent value="upcoming" className="space-y-4 pt-3">
            {upcomingJobs.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                {language === 'sv' ? 'Inga kommande jobb just nu.' : 'No upcoming jobs right now.'}
              </div>
            ) : (
              groupByDate(upcomingJobs).map(([date, visits]) => (
                <div key={date} className="space-y-2">
                  <div className="text-sm font-medium">{date}</div>
                  <div className="space-y-2">
                    {visits.map((visit) => (
                      <VisitCard key={visit.id} visit={visit} language={language} onClick={() => setSelectedVisitId(visit.id)} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </TabsContent>

          <TabsContent value="jobs" className="space-y-3 pt-3">
            {completedJobs.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                {language === 'sv' ? 'Inga avslutade jobb ännu.' : 'No completed jobs yet.'}
              </div>
            ) : (
              completedJobs.map((visit) => (
                <VisitCard key={visit.id} visit={visit} language={language} onClick={() => setSelectedVisitId(visit.id)} />
              ))
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}